/* eslint-disable max-statements */
import { Alert, Button, FormControl, FormGroup, FormLabel, Modal, Spinner, Table } from "react-bootstrap";
import { addDays, addWeeks } from "date-fns";
import { format, formatInTimeZone } from "date-fns-tz";
import { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { clone } from "lodash";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";

import { duplicateEvent } from "../../pages/facilitators/services/duplicateEvent";
import { getTimezone } from "../../helpers/dates";
import { useNotifications } from "../NotificationsContext";

const inputFormat = "yyyy-MM-dd'T'HH:mm";
const confirmFormat = "EEEE, LLLL do, yyyy 'at' h:mm bb 'EST'";

const DuplicateCourseModal = ({
	event,
	onCancel,
	show
}) => {
	const input = useRef();

	const {
		mutateAsync: duplicate
	} = duplicateEvent(useMutation);
	const { addNotification } = useNotifications();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const [newDate, setNewDate] = useState(null);
	const [newEventId, setNewEventId] = useState(null);
	const [confirm, setConfirm] = useState(false);

	const suggestedDate = useMemo(() => event?.start
		? formatInTimeZone(addWeeks(new Date(event.start), 1), getTimezone(), inputFormat)
		: null, [event]);
	const min = useMemo(() => format(addDays(new Date(), 1), inputFormat), []);
	const handleChange = useCallback(({ target }) => setNewDate(() => target.value || suggestedDate), [setNewDate]);

	const handleSchedule = useCallback(async (evt) => {
		evt.preventDefault();

		setLoading(true);
		setNewEventId(null);
		try {
			const res = await duplicate({
				courseID: event.id,
				date: newDate || suggestedDate
			});
			const { newCourseID, message, success } = res;

			if (success) {
				setNewEventId(newCourseID);
				addNotification({
					message,
					type: "success"
				});
			} else {
				addNotification({
					message,
					type: "danger"
				});
			}
		} catch (error) {
			addNotification({
				message: error.message,
				type: "danger"
			});
		}

		setConfirm(false);
		setLoading(false);
	}, [newDate, event]);

	const handleCancel = useCallback(() => {
		setNewEventId(() => null);
		if (onCancel) {
			onCancel();
		}
	}, [onCancel]);

	const handledEntered = useCallback(() => {
		input.current.focus();
	}, []);

	const handleViewNewEvent = useCallback((evt) => {
		evt.preventDefault();
		onCancel();

		const eventId = clone(newEventId);
		setNewEventId(() => null);

		navigate(`/class/${eventId}`);
	}, [onCancel, newEventId, setNewEventId]);

	const handleConfirm = useCallback(() => {
		setNewEventId(() => null);
		setConfirm(() => true);
	}, [setConfirm, setNewEventId]);

	const handleReset = useCallback(() => {
		setNewEventId(() => null);
		input.current.focus();
	}, [setNewEventId]);

	const cancelConfirm = useCallback(() => {
		setConfirm(() => false);
		input.current.focus();
	}, [setConfirm]);

	return (
		<Modal centered onEntered={handledEntered} show={show} size="lg">
			<Modal.Header>
				<Modal.Title>Schedule New Event</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<h2>{event?.title}</h2>

				<FormGroup>
					<FormLabel>New Date</FormLabel>
					<FormControl
						defaultValue={suggestedDate}
						min={min}
						onChange={handleChange}
						readOnly={confirm || newEventId}
						ref={input}
						type="datetime-local"
					/>
				</FormGroup>

				{
					newEventId
						? (
							<Alert className="mt-3" variant="success">
								<p>New event instance scheduled successfully.</p>
								<Button onClick={handleViewNewEvent} size="sm" variant="success">View New Event</Button>
								<Button className="ms-5" onClick={handleReset} size="sm" variant="secondary">Schedule Another</Button>
							</Alert>
						)
						: null
				}

			</Modal.Body>
			<Modal.Footer className="justify-content-between d-flex">
				<Button disabled={loading || confirm} onClick={handleCancel} variant="link">Cancel</Button>
				{newEventId
					? <Button onClick={handleCancel} variant="primary">Done</Button>
					: (
						<Button disabled={loading || confirm} onClick={handleConfirm} variant="success">
							{loading ? <Spinner animation="border" size="sm" /> : "Schedule"}
						</Button>
					)}
			</Modal.Footer>
			{ confirm
				? (
					<Modal.Footer>
						<Alert className="w-100" variant="warning">
							<div className="mb-4">
								<p><strong>Please confirm the following new event details:</strong></p>
								<Table borderless>
									<tbody>
										<tr>
											<th>Event Name:</th>
											<td>{event.title}</td>
										</tr>
										<tr>
											<th>New Event Date:</th>
											<td>{format(new Date(newDate || suggestedDate), confirmFormat)}</td>
										</tr>
									</tbody>
								</Table>
							</div>
							<div className="d-flex justify-content-between align-items-center">
								<Button disabled={loading} onClick={cancelConfirm} variant="danger">Cancel</Button>
								<Button disabled={loading} onClick={handleSchedule} variant="warning">
									{loading ? <Spinner animation="border" size="sm" /> : "Confirm"}
								</Button>
							</div>
						</Alert>
					</Modal.Footer>
				) : null}
		</Modal>
	);
};

DuplicateCourseModal.propTypes = {
	event: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		start: PropTypes.string,
		title: PropTypes.string
	}),
	onCancel: PropTypes.func,
	show: PropTypes.bool
};

DuplicateCourseModal.defaultProps = {
	event: null,
	onCancel: () => null,
	show: false
};

export default DuplicateCourseModal;
